import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useUpdateEmployee, useEmployee, useRoles } from '../../services/httpServices/queries';
import { useDepartments } from '../../services/httpServices/queries/useDepartments';
import { useActiveDesignations } from '../../services/httpServices/queries/useDesignations';
import { mediaService } from '../../services/httpServices/mediaService';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { PayrollPolicySelect } from '../../components/ui/PayrollPolicySelect';
import { WeeklyOffDaysPicker } from '../../components/ui/weekly-off-days-picker';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { InfoHint } from '../../components/ui/InfoHint';
import { DateOfBirthInput } from '../../components/ui/date-of-birth-input';
import { DatePicker } from '../../components/ui/DatePicker';
import { FormField, FormSection } from '../../components/ui/form-section';
import { ASSIGNABLE_SYSTEM_ROLES } from '../../hooks/usePermissions';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  X,
  Upload,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Users,
  CreditCard,
  Save,
  Check,
  Shield,
  PenLine,
  Briefcase,
  Wallet,
  Phone,
  Landmark,
  Image as ImageIcon,
  LayoutDashboard,
} from 'lucide-react';

// SUPER_ADMIN is excluded — it is reserved for the master developer and is
// managed from the Developer Console (/settings/developer).
const ALL_SYSTEM_ROLES: string[] = ASSIGNABLE_SYSTEM_ROLES;

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Full years between a date-of-birth string and today. Returns '' when unset. */
function calculateAge(dob: string): number | '' {
  if (!dob) return '';
  const today = new Date();
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '';
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age < 0 ? '' : age;
}

/**
 * Estimated date of birth (Jan 1 of the birth year) for an employee who only
 * knows their age. Mirrors the patient registration behaviour.
 */
function deriveDateOfBirthFromAge(age: number): string {
  const year = new Date().getFullYear() - age;
  return `${year}-01-01`;
}

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERN', label: 'Intern' },
];

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];


export function EmployeeEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // When the form was opened from the Users screen, return there instead of the
  // employee detail page.
  const from = (location.state as { from?: string } | null)?.from;
  const backTo = from || `/employees/${id}`;
  const backLabel = from ? 'Back to Users' : 'Back to Employee';
  const updateEmployee = useUpdateEmployee();
  const { data: employeeData, isLoading: employeeLoading, error: employeeError } = useEmployee(id || '');
  const { data: departmentsData, isLoading: departmentsLoading } = useDepartments();
  const { data: designationsData, isLoading: designationsLoading } = useActiveDesignations();
  const { data: rolesData } = useRoles();

  const employee = employeeData?.data;

  const departmentOptions = (departmentsData?.data || []).map((d: any) => ({
    value: d.id,
    label: `${d.name} (${d.code})`,
  }));

  const designationOptions = (designationsData?.data || []).map((d: any) => ({
    value: d.id,
    label: d.name,
  }));

  const [formData, setFormData] = useState({
    employeeCode: '',
    type: 'admin',
    firstName: '',
    lastName: '',
    name: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    mobileNumber: '',
    email: '',
    address: '',
    departmentId: '',
    designationId: '',
    joiningDate: '',
    employmentType: '',
    basicSalary: '',
    allowanceItems: [] as { id: string; label: string; amount: string }[],
    taxDeduction: '',
    payrollPolicyId: '',
    weeklyOffDays: null as number[] | null,
    workingDaysPerMonth: '',
    payrollEnabled: true,
    bankAccountNumber: '',
    bankName: '',
    nationalIdFrontUrl: '',
    nationalIdBackUrl: '',
    photoUrl: '',
    signatureUrl: '',
    isActive: true,
    dashboardAccess: false,
    roles: [] as string[],
    customRoleIds: [] as string[],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [nidFrontPreview, setNidFrontPreview] = useState<string | null>(null);
  const [nidBackPreview, setNidBackPreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  // Newly selected replacements, uploaded to storage on save. Existing images
  // keep their stored URLs; only replacements are uploaded.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [nidFrontFile, setNidFrontFile] = useState<File | null>(null);
  const [nidBackFile, setNidBackFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPhotoDragOver, setIsPhotoDragOver] = useState(false);
  const [isNidFrontDragOver, setIsNidFrontDragOver] = useState(false);
  const [isNidBackDragOver, setIsNidBackDragOver] = useState(false);
  const [isSignatureDragOver, setIsSignatureDragOver] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const nidFrontInputRef = useRef<HTMLInputElement>(null);
  const nidBackInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (employee) {
      setFormData({
        employeeCode: employee.employeeCode || '',
        type: employee.type || 'admin',
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        name: employee.name || '',
        dateOfBirth: employee.dateOfBirth || '',
        age: (() => {
          const computed = calculateAge(employee.dateOfBirth || '');
          return computed === '' ? '' : String(computed);
        })(),
        gender: employee.gender || '',
        mobileNumber: employee.mobileNumber || '',
        email: employee.email || '',
        address: employee.address || '',
        departmentId: employee.departmentId || '',
        designationId: employee.designationId || '',
        joiningDate: employee.joiningDate || '',
        employmentType: employee.employmentType || '',
        basicSalary: employee.basicSalary ? String(employee.basicSalary) : '',
        allowanceItems: (employee.allowanceItems || []).map((a: any) => ({
          id: crypto.randomUUID(),
          label: a.label || '',
          amount: a.amount !== undefined && a.amount !== null ? String(a.amount) : '',
        })),
        taxDeduction: employee.taxDeduction ? String(employee.taxDeduction) : '',
        payrollPolicyId: employee.payrollPolicyId || '',
        weeklyOffDays: employee.weeklyOffDays ?? null,
        workingDaysPerMonth:
          employee.workingDaysPerMonth !== null && employee.workingDaysPerMonth !== undefined
            ? String(employee.workingDaysPerMonth)
            : '',
        payrollEnabled: employee.payrollEnabled !== false,
        bankAccountNumber: employee.bankAccountNumber || '',
        bankName: employee.bankName || '',
        nationalIdFrontUrl: employee.nationalIdFrontUrl || '',
        nationalIdBackUrl: employee.nationalIdBackUrl || '',
        photoUrl: employee.photoUrl || '',
        signatureUrl: employee.signatureUrl || '',
        isActive: employee.isActive ?? true,
        dashboardAccess: employee.user?.dashboardAccess === true,
        roles: employee.user?.roles || [],
        customRoleIds: employee.user?.customRoles?.map((r: any) => r.id) || [],
      });
      setPhotoPreview(employee.photoUrl || null);
      setNidFrontPreview(employee.nationalIdFrontUrl || null);
      setNidBackPreview(employee.nationalIdBackUrl || null);
      setSignaturePreview(employee.signatureUrl || null);
    }
  }, [employee]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!String(formData.age).trim()) {
      errors.age = 'Age is required';
    } else {
      const ageNum = Number(formData.age);
      if (Number.isNaN(ageNum) || ageNum < 0 || ageNum > 120) {
        errors.age = 'Enter a valid age';
      }
    }
    if (!formData.designationId) errors.designationId = 'Designation is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    if (formData.basicSalary) {
      const num = parseFloat(formData.basicSalary);
      if (isNaN(num) || num < 0) {
        errors.basicSalary = 'Must be a positive number';
      }
    }
    if (formData.taxDeduction) {
      const num = parseFloat(formData.taxDeduction);
      if (isNaN(num) || num < 0) {
        errors.taxDeduction = 'Must be a positive number';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !id) return;

    const allowanceItems = formData.allowanceItems
      .map((a) => ({ label: a.label.trim(), amount: Number(a.amount) || 0 }))
      .filter((a) => a.label || a.amount);

    const data: any = {
      ...formData,
      name: `${formData.firstName} ${formData.lastName}`,
      basicSalary: formData.basicSalary ? parseFloat(formData.basicSalary) : undefined,
      allowanceItems,
      taxDeduction: formData.taxDeduction ? parseFloat(formData.taxDeduction) : undefined,
      payrollPolicyId: formData.payrollPolicyId || null,
      departmentId: formData.departmentId || null,
      weeklyOffDays: formData.weeklyOffDays,
      workingDaysPerMonth: formData.workingDaysPerMonth
        ? parseFloat(formData.workingDaysPerMonth)
        : null,
    };
    if (data.customRoleIds === undefined || data.customRoleIds === null) delete data.customRoleIds;
    // Age is a UI-only derivation — the API persists the date of birth only.
    delete data.age;
    try {
      setIsUploading(true);
      // Upload any newly selected images first, then persist only their URLs.
      if (photoFile) {
        data.photoUrl = (await mediaService.uploadImage(photoFile, 'employee')).data.url;
      }
      if (nidFrontFile) {
        data.nationalIdFrontUrl = (await mediaService.uploadImage(nidFrontFile, 'employee')).data.url;
      }
      if (nidBackFile) {
        data.nationalIdBackUrl = (await mediaService.uploadImage(nidBackFile, 'employee')).data.url;
      }
      if (signatureFile) {
        data.signatureUrl = (await mediaService.uploadImage(signatureFile, 'employee')).data.url;
      }
      await updateEmployee.mutateAsync({ id, data });
      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate(backTo);
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Keep the date of birth and age in sync: typing an age estimates the
      // date of birth, while picking a date recalculates the age.
      if (field === 'dateOfBirth') {
        const computed = calculateAge(value as string);
        next.age = computed === '' ? '' : String(computed);
      }
      if (field === 'age') {
        const numeric = value === '' || value === null || value === undefined ? NaN : Number(value);
        next.dateOfBirth =
          !Number.isNaN(numeric) && numeric >= 0 ? deriveDateOfBirthFromAge(numeric) : '';
      }
      return next;
    });
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Allowance item handlers
  const addAllowance = () => {
    setFormData((prev) => ({
      ...prev,
      allowanceItems: [
        ...prev.allowanceItems,
        { id: crypto.randomUUID(), label: '', amount: '' },
      ],
    }));
  };

  const updateAllowance = (id: string, field: 'label' | 'amount', value: string) => {
    setFormData((prev) => ({
      ...prev,
      allowanceItems: prev.allowanceItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeAllowance = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      allowanceItems: prev.allowanceItems.filter((item) => item.id !== id),
    }));
  };

  // Photo handlers
  const handlePhotoDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  }, []);

  const handlePhotoDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(true);
  }, []);

  const handlePhotoDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPhotoDragOver(false);
  }, []);

  const handlePhotoFile = (file: File) => {
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handlePhotoFile(file);
    }
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    updateField('photoUrl', '');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // NID Front handlers
  const handleNidFrontDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidFrontDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleNidFrontFile(file);
    }
  }, []);

  const handleNidFrontDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidFrontDragOver(true);
  }, []);

  const handleNidFrontDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidFrontDragOver(false);
  }, []);

  const handleNidFrontFile = (file: File) => {
    setNidFrontFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setNidFrontPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNidFrontSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleNidFrontFile(file);
    }
  };

  const clearNidFront = () => {
    setNidFrontFile(null);
    setNidFrontPreview(null);
    updateField('nationalIdFrontUrl', '');
    if (nidFrontInputRef.current) nidFrontInputRef.current.value = '';
  };

  // NID Back handlers
  const handleNidBackDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidBackDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleNidBackFile(file);
    }
  }, []);

  const handleNidBackDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidBackDragOver(true);
  }, []);

  const handleNidBackDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNidBackDragOver(false);
  }, []);

  const handleNidBackFile = (file: File) => {
    setNidBackFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setNidBackPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNidBackSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleNidBackFile(file);
    }
  };

  const clearNidBack = () => {
    setNidBackFile(null);
    setNidBackPreview(null);
    updateField('nationalIdBackUrl', '');
    if (nidBackInputRef.current) nidBackInputRef.current.value = '';
  };

  // Signature handlers
  const handleSignatureDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSignatureDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleSignatureFile(file);
    }
  }, []);

  const handleSignatureDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSignatureDragOver(true);
  }, []);

  const handleSignatureDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSignatureDragOver(false);
  }, []);

  const handleSignatureFile = (file: File) => {
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setSignaturePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSignatureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleSignatureFile(file);
    }
  };

  const clearSignature = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    updateField('signatureUrl', '');
    if (signatureInputRef.current) signatureInputRef.current.value = '';
  };

  const inputBase =
    'w-full h-9 px-3 border rounded-lg bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary/20 focus:border-primary';
  const inputInvalid = 'border-danger focus:ring-danger/20 focus:border-danger';
  const labelClass =
    'mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  const allowanceTotal = formData.allowanceItems.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0
  );
  const mainSalary =
    (Number(formData.basicSalary) || 0) + allowanceTotal - (Number(formData.taxDeduction) || 0);

  const renderImageUploader = (
    label: string,
    preview: string | null,
    isDragOver: boolean,
    inputRef: React.RefObject<HTMLInputElement | null>,
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void,
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void,
    onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void,
    onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onClear: () => void,
    dropZoneClass: string = 'w-full h-[180px]',
    imageFit: 'cover' | 'contain' = 'cover',
    emptyIcon?: React.ReactNode,
  ) => (
    <div>
      <label className={labelClass}>{label}</label>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`relative ${dropZoneClass} rounded-xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center overflow-hidden transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : preview
              ? 'border-success bg-success/5'
              : 'border-border bg-background hover:border-primary/50 hover:bg-primary/5'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onSelect}
        />
        {preview ? (
          <>
            <img
              src={preview}
              alt={label}
              className={`absolute inset-0 w-full h-full ${
                imageFit === 'contain' ? 'object-contain p-3 bg-white' : 'object-cover'
              }`}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute top-2 right-2 p-1.5 bg-danger text-white rounded-lg shadow-sm hover:bg-danger-dark transition-colors z-10"
              title={`Remove ${label}`}
            >
              <Trash2 size={14} />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
              <p className="text-xs text-white font-medium text-center">Click or drop to replace</p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 p-4 text-text-muted">
            <div className={`p-3 rounded-full transition-colors ${isDragOver ? 'bg-primary/10' : 'bg-background'}`}>
              {isDragOver ? <Upload size={24} className="text-primary" /> : (emptyIcon ?? <CreditCard size={24} />)}
            </div>
            <p className="text-xs text-center font-medium">
              {isDragOver ? 'Drop image here' : 'Drag & drop or click'}
            </p>
            <p className="text-[10px] text-center opacity-70">JPG, PNG, WEBP up to 5MB</p>
          </div>
        )}
      </div>
    </div>
  );

  if (employeeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (employeeError || !employee) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load employee details. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 pb-2">
      {/* ─── Page header ─── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
            title={backLabel}
          >
            <ArrowLeft size={17} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-xl font-bold leading-tight text-text">Edit Employee</h1>
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                {formData.employeeCode || 'No code'}
              </span>
            </div>
            <p className="truncate text-xs text-text-muted">
              {`${formData.firstName} ${formData.lastName}`.trim() || 'Employee profile'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => updateField('isActive', !formData.isActive)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            formData.isActive
              ? 'border-success/20 bg-success/10 text-success'
              : 'border-danger/20 bg-danger/10 text-danger'
          }`}
          title="Toggle account status"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${formData.isActive ? 'bg-success' : 'bg-danger'}`} />
          {formData.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>

      {/* No linked user warning */}
      {!employee.user && (
        <div className="flex items-start gap-2 rounded-xl border border-warning/20 bg-warning/10 p-3 text-warning">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span className="text-xs font-medium leading-relaxed">
            This employee does not have a linked system user account yet. Saving will create and link one,
            then apply the selected roles.
          </span>
        </div>
      )}

      {/* General error */}
      {formErrors.general && (
        <div className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-danger">
          <AlertCircle size={16} className="shrink-0" />
          <span className="text-xs font-medium">{formErrors.general}</span>
        </div>
      )}

      <form
        id="employee-edit-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]"
      >
        {/* ═══ Main column ═══ */}
        <div className="min-w-0 space-y-4">
          {/* ─── Personal information ─── */}
          <FormSection
            icon={<Users size={16} />}
            title="Personal Information"
            description="Identity and basic profile details"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <FormField label="First Name" required error={formErrors.firstName}>
                  <input
                    className={ic('firstName')}
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    placeholder="John"
                  />
                </FormField>
                <FormField label="Last Name" required error={formErrors.lastName}>
                  <input
                    className={ic('lastName')}
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    placeholder="Doe"
                  />
                </FormField>
                <FormField label="Employee Code">
                  <input
                    className={`${inputBase} cursor-not-allowed border-border bg-background/50 text-text-muted`}
                    value={formData.employeeCode}
                    readOnly
                    disabled
                  />
                </FormField>
                <FormField label="Gender" error={formErrors.gender}>
                  <select
                    className={ic('gender')}
                    value={formData.gender}
                    onChange={(e) => updateField('gender', e.target.value)}
                  >
                    <option value="">Select Gender</option>
                    {genderOptions.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </FormField>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className={labelClass}>Date of Birth &amp; Age</label>
                  <div className="flex gap-2">
                    <div className="min-w-0 flex-1">
                      <DateOfBirthInput
                        className={ic('dateOfBirth')}
                        value={formData.dateOfBirth}
                        onChange={(value) => updateField('dateOfBirth', value)}
                      />
                    </div>
                    <div className="w-[72px] shrink-0">
                      <input
                        type="number"
                        min={0}
                        max={120}
                        className={ic('age')}
                        value={formData.age}
                        onChange={(e) => updateField('age', e.target.value)}
                        placeholder="Age"
                        title="Typing an age estimates the date of birth"
                      />
                    </div>
                  </div>
                {err('age') || err('dateOfBirth')}
              </div>
            </div>
          </FormSection>

          {/* ─── Employment ─── */}
          <FormSection
            icon={<Briefcase size={16} />}
            title="Employment"
            description="Department, designation and salary base"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Department">
                <SearchableSelect
                  value={formData.departmentId}
                  onChange={(v) => updateField('departmentId', v)}
                  options={departmentOptions}
                  placeholder="Select Department"
                  loading={departmentsLoading}
                  error={err('departmentId')}
                />
              </FormField>
              <FormField label="Designation" required>
                <SearchableSelect
                  value={formData.designationId}
                  onChange={(v) => updateField('designationId', v)}
                  options={designationOptions}
                  placeholder="Select Designation"
                  loading={designationsLoading}
                  error={err('designationId')}
                />
              </FormField>
              <FormField label="Employment Type" error={formErrors.employmentType}>
                <select
                  className={ic('employmentType')}
                  value={formData.employmentType}
                  onChange={(e) => updateField('employmentType', e.target.value)}
                >
                  <option value="">Select Type</option>
                  {employmentTypeOptions.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Joining Date" error={formErrors.joiningDate}>
                <DatePicker
                  className={ic('joiningDate')}
                  value={formData.joiningDate}
                  onChange={(value) => updateField('joiningDate', value)}
                />
              </FormField>
            </div>
          </FormSection>

          {/* ─── Contact & bank ─── */}
          <FormSection
            icon={<Phone size={16} />}
            title="Contact & Bank"
            description="How to reach the employee and where to pay them"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Mobile Number" error={formErrors.mobileNumber}>
                <input
                  type="tel"
                  className={ic('mobileNumber')}
                  value={formData.mobileNumber}
                  onChange={(e) => updateField('mobileNumber', e.target.value)}
                  placeholder="+8801234567890"
                />
              </FormField>
              <FormField label="Email" error={formErrors.email}>
                <input
                  type="email"
                  className={ic('email')}
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="employee@hospital.com"
                />
              </FormField>
              <FormField label="Address" error={formErrors.address} className="sm:col-span-2">
                <input
                  className={ic('address')}
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  placeholder="123 Hospital Road, Dhaka"
                />
              </FormField>
            </div>

            <div className="my-4 border-t border-border/70" />
            <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              <Landmark size={13} className="text-primary" />
              Bank Details
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Bank Account Number" error={formErrors.bankAccountNumber}>
                <input
                  className={ic('bankAccountNumber')}
                  value={formData.bankAccountNumber}
                  onChange={(e) => updateField('bankAccountNumber', e.target.value)}
                  placeholder="1234567890"
                />
              </FormField>
              <FormField label="Bank Name" error={formErrors.bankName}>
                <input
                  className={ic('bankName')}
                  value={formData.bankName}
                  onChange={(e) => updateField('bankName', e.target.value)}
                  placeholder="Sonali Bank"
                />
              </FormField>
            </div>
          </FormSection>

          {/* ─── Compensation & payroll ─── */}
          <FormSection
            icon={<Wallet size={16} />}
            title="Compensation & Payroll"
            description="Salary, allowances, tax and payroll rules"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <FormField label="Gross Salary" error={formErrors.basicSalary}>
                <ValidatedNumberInput
                  className={ic('basicSalary')}
                  value={formData.basicSalary}
                  onChange={(v) => updateField('basicSalary', String(v))}
                  placeholder="50000"
                />
              </FormField>
              <FormField label="Tax (monthly)" error={formErrors.taxDeduction}>
                <ValidatedNumberInput
                  className={ic('taxDeduction')}
                  value={formData.taxDeduction}
                  onChange={(v) => updateField('taxDeduction', String(v))}
                  placeholder="0"
                />
              </FormField>
              <div className="sm:col-span-2 lg:col-span-1">
                <div className={labelClass}>
                  <span>Payroll Policy</span>
                  <InfoHint
                    label="Payroll Policy"
                    en="Leave empty to inherit the department policy (or the global default). Choose a policy here to override it for this employee only — useful when someone needs different weekly off days or rates."
                    bn="খালি রাখলে বিভাগীয় নীতিমালা (বা বৈশ্বিক ডিফল্ট) প্রযোজ্য হবে। শুধুমাত্র এই কর্মীর জন্য আলাদা নীতিমালা প্রযোজ্য করতে এখানে একটি নির্বাচন করুন — যেমন কারও সাপ্তাহিক বন্ধ বা হার ভিন্ন হলে।"
                  />
                </div>
                <PayrollPolicySelect
                  value={formData.payrollPolicyId}
                  onChange={(v) => updateField('payrollPolicyId', v)}
                  error={err('payrollPolicyId')}
                />
                <p className="mt-1 text-[11px] text-text-muted">
                  Leave empty to inherit the department / default policy.
                </p>
              </div>
            </div>

            {/* Allowances */}
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className={labelClass}>Allowances</label>
                <button
                  type="button"
                  onClick={addAllowance}
                  className="inline-flex items-center gap-1 rounded-lg border border-primary/30 px-2.5 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-primary/5"
                >
                  <Plus size={12} /> Add Allowance
                </button>
              </div>
              {formData.allowanceItems.length === 0 ? (
                <p className="text-[11px] italic text-text-muted">No allowances added</p>
              ) : (
                <div className="space-y-2">
                  {formData.allowanceItems.map((item) => (
                    <div key={item.id} className="grid grid-cols-[1fr_6.5rem_auto] items-center gap-2">
                      <input
                        className={`${inputBase} ${inputValid}`}
                        value={item.label}
                        onChange={(e) => updateAllowance(item.id, 'label', e.target.value)}
                        placeholder="Allowance name (e.g. House Rent)"
                      />
                      <ValidatedNumberInput
                        className={`${inputBase} ${inputValid}`}
                        value={item.amount}
                        onChange={(v) => updateAllowance(item.id, 'amount', String(v))}
                        placeholder="Amount"
                      />
                      <button
                        type="button"
                        onClick={() => removeAllowance(item.id)}
                        className="rounded-lg p-2 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                        title="Remove allowance"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="my-4 border-t border-border/70" />

            <div>
              <div className={labelClass}>
                <span>Working days / month</span>
                <InfoHint
                  label="Working days / month"
                  en="The divisor used to turn the monthly salary into a daily rate. For example 20,000 ÷ 26 = 769.23 per day. Leave blank to inherit the payroll policy (26 by default)."
                  bn="মাসিক বেতনকে দৈনিক হারে রূপান্তরের ভাজক। উদাহরণ: ২০,০০০ ÷ ২৬ = প্রতিদিন ৭৬৯.২৩ টাকা। খালি রাখলে পে-রোল নীতিমালা (ডিফল্ট ২৬) প্রযোজ্য হবে।"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-28 shrink-0">
                  <ValidatedNumberInput
                    className={ic('workingDaysPerMonth')}
                    value={formData.workingDaysPerMonth}
                    onChange={(v) => updateField('workingDaysPerMonth', String(v))}
                    placeholder="26"
                  />
                </div>
                <p className="text-[11px] text-text-muted">
                  {Number(formData.workingDaysPerMonth) > 0 && Number(formData.basicSalary) > 0
                    ? `৳${(
                        Number(formData.basicSalary) / Number(formData.workingDaysPerMonth)
                      ).toLocaleString(undefined, { maximumFractionDigits: 2 })} per day`
                    : 'Blank inherits the policy divisor.'}
                </p>
              </div>
            </div>

            {/* Salary summary */}
            <div className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4">
              <div className="bg-surface px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Gross</p>
                <p className="text-sm font-semibold text-text">
                  ৳{(Number(formData.basicSalary) || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-surface px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Allowances</p>
                <p className="text-sm font-semibold text-text">৳{allowanceTotal.toLocaleString()}</p>
              </div>
              <div className="bg-surface px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Tax</p>
                <p className="text-sm font-semibold text-danger">
                  −৳{(Number(formData.taxDeduction) || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-success/5 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-success">Main Salary</p>
                <p className="text-sm font-semibold text-success">
                  ৳{mainSalary.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </FormSection>

          {/* ─── Documents ─── */}
          <FormSection
            icon={<ImageIcon size={16} />}
            title="Documents"
            description="National ID and signature — optional"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {renderImageUploader(
                'NID Front',
                nidFrontPreview,
                isNidFrontDragOver,
                nidFrontInputRef,
                handleNidFrontDrop,
                handleNidFrontDragOver,
                handleNidFrontDragLeave,
                handleNidFrontSelect,
                clearNidFront,
                'w-full h-[150px]',
              )}
              {renderImageUploader(
                'NID Back',
                nidBackPreview,
                isNidBackDragOver,
                nidBackInputRef,
                handleNidBackDrop,
                handleNidBackDragOver,
                handleNidBackDragLeave,
                handleNidBackSelect,
                clearNidBack,
                'w-full h-[150px]',
              )}
              {renderImageUploader(
                'Signature',
                signaturePreview,
                isSignatureDragOver,
                signatureInputRef,
                handleSignatureDrop,
                handleSignatureDragOver,
                handleSignatureDragLeave,
                handleSignatureSelect,
                clearSignature,
                'w-full h-[150px]',
                'contain',
                <PenLine size={24} />,
              )}
            </div>
            <p className="mt-3 text-[11px] text-text-muted">
              The signature can be stamped onto HR letters, payslips and other documents.
            </p>
          </FormSection>
        </div>

        {/* ═══ Sidebar ═══ */}
        <aside className="space-y-4">
          {/* Profile photo */}
          <FormSection
            icon={<ImageIcon size={16} />}
            title="Profile Photo"
            description="Shown across the employee profile"
          >
            <div className="mx-auto w-[168px]">
              {renderImageUploader(
                'Photo',
                photoPreview,
                isPhotoDragOver,
                photoInputRef,
                handlePhotoDrop,
                handlePhotoDragOver,
                handlePhotoDragLeave,
                handlePhotoSelect,
                clearPhoto,
                'w-full h-[200px]',
              )}
            </div>
          </FormSection>

          {/* Account status */}
          <FormSection
            icon={<ToggleRight size={16} />}
            title="Account Status"
            description="Access and visibility"
          >
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    formData.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                  }`}
                >
                  {formData.isActive ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </p>
                  <p className="text-[11px] leading-snug text-text-muted">
                    {formData.isActive
                      ? 'Shown in rosters and payroll.'
                      : 'Hidden from rosters and payroll.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.isActive}
                onClick={() => updateField('isActive', !formData.isActive)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  formData.isActive ? 'bg-success' : 'bg-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    formData.isActive ? 'translate-x-[24px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
            {!employee.user && (
              <p className="mt-2 rounded-lg border border-info/20 bg-info/10 p-2 text-[11px] leading-relaxed text-info">
                No system user yet — one will be created and linked on save.
              </p>
            )}
          </FormSection>

          {/* Dashboard access */}
          <FormSection
            icon={<LayoutDashboard size={16} />}
            title="Dashboard Access"
            description="Allow this employee to sign in"
          >
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    formData.dashboardAccess
                      ? 'bg-success/10 text-success'
                      : 'bg-background text-text-muted'
                  }`}
                >
                  <LayoutDashboard size={17} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">
                    {formData.dashboardAccess ? 'Access Allowed' : 'Access Denied'}
                  </p>
                  <p className="text-[11px] leading-snug text-text-muted">
                    {formData.dashboardAccess
                      ? 'The linked user can sign in to the dashboard.'
                      : 'The linked user cannot sign in.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.dashboardAccess}
                onClick={() => updateField('dashboardAccess', !formData.dashboardAccess)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  formData.dashboardAccess ? 'bg-success' : 'bg-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    formData.dashboardAccess ? 'translate-x-[24px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
          </FormSection>

          {/* Payroll settings */}
          <FormSection
            icon={<Wallet size={16} />}
            title="Payroll Settings"
            description="Weekly offs and payroll eligibility"
          >
            <div>
              <div className={labelClass}>
                <span>Weekly Off Days</span>
                <InfoHint
                  label="Weekly Off Days"
                  en="This employee's paid weekly off days. They override the payroll policy so different employees can have different weekends. Off days are never deducted as absence."
                  bn="এই কর্মীর সাপ্তাহিক বন্ধের দিন। এটি পে-রোল নীতিমালাকে ওভাররাইড করে, যাতে ভিন্ন কর্মীর ভিন্ন সাপ্তাহিক ছুটি থাকতে পারে। বন্ধের দিন কখনো অনুপস্থিতি হিসেবে কাটা হয় না।"
                />
              </div>
              <WeeklyOffDaysPicker
                value={formData.weeklyOffDays}
                onChange={(days) => updateField('weeklyOffDays', days)}
              />
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    formData.payrollEnabled
                      ? 'bg-success/10 text-success'
                      : 'bg-background text-text-muted'
                  }`}
                >
                  <Wallet size={17} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">Enable Payroll</p>
                  <p className="text-[11px] leading-snug text-text-muted">
                    When off, this employee is skipped when generating monthly payroll.
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData.payrollEnabled}
                onClick={() => updateField('payrollEnabled', !formData.payrollEnabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  formData.payrollEnabled ? 'bg-success' : 'bg-border'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                    formData.payrollEnabled ? 'translate-x-[24px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
          </FormSection>

          {/* System roles */}
          <FormSection
            icon={<Shield size={16} />}
            title="System Roles"
            description="Built-in dashboard access"
          >
            <div className="flex min-h-[26px] flex-wrap gap-1.5">
              {formData.roles.length === 0 ? (
                <span className="text-[11px] italic text-text-muted">No system roles assigned</span>
              ) : (
                formData.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-medium text-white shadow-sm"
                  >
                    {getRoleLabel(role)}
                    <button
                      type="button"
                      onClick={() => updateField('roles', formData.roles.filter((r) => r !== role))}
                      className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                      title={`Remove ${getRoleLabel(role)}`}
                    >
                      <X size={10} strokeWidth={3} />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="mt-2.5 max-h-44 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                Add roles
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_SYSTEM_ROLES.map((role) => {
                  if (formData.roles.includes(role)) return null;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => updateField('roles', [...formData.roles, role])}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                    >
                      <Plus size={10} />
                      {getRoleLabel(role)}
                    </button>
                  );
                })}
                {ALL_SYSTEM_ROLES.every((r) => formData.roles.includes(r)) && (
                  <span className="text-[11px] italic text-text-muted">All system roles assigned</span>
                )}
              </div>
            </div>
          </FormSection>

          {/* Custom roles */}
          <FormSection
            icon={<Users size={16} />}
            title="Custom Roles"
            description="Additional permission sets"
          >
            <div className="flex min-h-[26px] flex-wrap gap-1.5">
              {formData.customRoleIds.length === 0 ? (
                <span className="text-[11px] italic text-text-muted">No custom roles assigned</span>
              ) : (
                formData.customRoleIds.map((id: string) => {
                  const role = (rolesData?.data || []).find((r: any) => r.id === id);
                  if (!role) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-medium text-white shadow-sm"
                    >
                      {role.name}
                      <button
                        type="button"
                        onClick={() =>
                          updateField(
                            'customRoleIds',
                            formData.customRoleIds.filter((rid) => rid !== id),
                          )
                        }
                        className="rounded-full p-0.5 transition-colors hover:bg-white/20"
                        title={`Remove ${role.name}`}
                      >
                        <X size={10} strokeWidth={3} />
                      </button>
                    </span>
                  );
                })
              )}
            </div>
            {(rolesData?.data || []).length > 0 ? (
              <div className="mt-2.5 max-h-44 overflow-y-auto rounded-lg border border-border bg-background/50 p-2.5">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                  Add roles
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(rolesData?.data || []).map((role: any) => {
                    if (formData.customRoleIds.includes(role.id)) return null;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() =>
                          updateField('customRoleIds', [...formData.customRoleIds, role.id])
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
                      >
                        <Plus size={10} />
                        {role.name}
                      </button>
                    );
                  })}
                  {(rolesData?.data || []).every((r: any) =>
                    formData.customRoleIds.includes(r.id),
                  ) && (
                    <span className="text-[11px] italic text-text-muted">All roles assigned</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-2 text-[11px] text-text-muted">
                No roles available. Create one in Users {'>'} Roles first.
              </p>
            )}
          </FormSection>
        </aside>

        {/* ─── Sticky footer actions ─── */}
        <div className="sticky bottom-0 z-20 flex items-center justify-between gap-3 rounded-xl border border-border bg-surface/95 px-3 py-2.5 shadow-lg backdrop-blur xl:col-span-2">
          <p className="hidden text-xs text-text-muted sm:block">
            Fields marked <span className="font-semibold text-danger">*</span> are required.
          </p>
          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="h-9 rounded-lg border border-border bg-surface px-3.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateEmployee.isPending || isUploading}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              {(updateEmployee.isPending || isUploading) && (
                <Loader2 size={15} className="animate-spin" />
              )}
              <Save size={15} />
              {isUploading ? 'Uploading…' : updateEmployee.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm animate-in space-y-4 rounded-2xl border border-border bg-surface p-6 text-center shadow-xl duration-200 fade-in zoom-in">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <Check size={28} className="text-success" strokeWidth={2.5} />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text">Changes Saved</h3>
              <p className="text-sm text-text-muted">
                Employee details have been updated successfully.
              </p>
            </div>
            <button
              onClick={handleSuccessClose}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
